import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Instagram, Youtube, Music2, MapPin, Music } from "lucide-react";
import { ARTIST } from "../mock";

export default function Contact() {
  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16" data-testid="contact-page">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">Accueil</Link>
        <span>/</span>
        <span className="text-white">Contact</span>
      </div>

      <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            05 · Restons en contact
          </div>
          <h1 className="font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            Écrivez-<span className="text-blue-500">moi</span>
          </h1>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Booking, presse, ou simple mot d'encouragement : je lis et je réponds personnellement à chaque message. Merci pour votre soutien.
          </p>
        </div>
      </div>

      {/* MAIN MAILTO CARD */}
      <a
        href={`mailto:${ARTIST.email}`}
        data-testid="main-mailto-card"
        className="block border border-white/15 hover:border-red-500/50 transition-all duration-500 p-8 md:p-14 mb-10 group relative overflow-hidden"
      >
        <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-blue-500/5 blur-3xl group-hover:bg-red-500/10 transition-all duration-700" />
        <div className="absolute -left-12 -bottom-12 w-64 h-64 rounded-full bg-red-500/5 blur-3xl group-hover:bg-blue-500/10 transition-all duration-700" />
        <div className="relative grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-9">
            <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4 flex items-center gap-2">
              <Mail size={12} /> Cliquez pour envoyer un mail
            </div>
            <div className="font-display text-4xl md:text-6xl lg:text-7xl uppercase tracking-tight break-all leading-[0.95]">
              {ARTIST.email}
            </div>
            <p className="mt-5 text-neutral-400 text-sm max-w-xl">
              Le bouton ouvre directement votre messagerie. Réponse personnelle sous 48 heures.
            </p>
          </div>
          <div className="md:col-span-3 flex md:justify-end">
            <span className="inline-flex items-center gap-2 bg-white text-black group-hover:bg-red-500 group-hover:text-white transition-colors duration-500 px-6 py-4 text-xs uppercase tracking-[0.2em]">
              Envoyer un mail <ArrowUpRight size={16} />
            </span>
          </div>
        </div>
      </a>

      {/* SECONDARY GRID */}
      <div className="grid md:grid-cols-3 gap-5" data-testid="contact-secondary-grid">
        {/* Booking */}
        <div className="border border-white/15 p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-500 mb-3">Booking · Direct</div>
          <div className="space-y-3">
            <a
              href={`mailto:${ARTIST.labelEmail}`}
              data-testid="booking-mailto"
              className="flex items-center gap-3 hover:text-white text-neutral-200 group"
            >
              <Mail size={16} className="text-neutral-500 group-hover:text-blue-500" />
              <span className="text-sm">{ARTIST.labelEmail}</span>
            </a>
          </div>
        </div>

        {/* Where */}
        <div className="border border-white/15 p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 mb-3">Basé à</div>
          <div className="flex items-start gap-3 text-neutral-200">
            <MapPin size={16} className="text-neutral-500 mt-0.5" />
            <div>
              <div className="text-sm">PACA & Bourgogne-Franche-Comté — France</div>
              <div className="text-xs text-neutral-500 mt-1">Disponible partout sur demande</div>
            </div>
          </div>
        </div>

        {/* Socials */}
        <div className="border border-white/15 p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-500 mb-3">Réseaux</div>
          <div className="flex flex-wrap gap-2">
            <a
              href={ARTIST.linktree}
              target="_blank"
              rel="noreferrer"
              data-testid="social-linktree"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-red-500 hover:text-red-500 transition px-3 py-2 text-xs uppercase tracking-[0.2em]"
            >
              <Music2 size={14} /> Linktree
            </a>
            <a
              href="https://www.instagram.com/vincerodi84"
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-red-500 hover:text-red-500 transition px-3 py-2 text-xs uppercase tracking-[0.2em]"
            >
              <Instagram size={14} /> Instagram
            </a>
            <a
              href="https://open.spotify.com/artist/5vjrwc9oDSp6pRqE4wJwsU"
              target="_blank"
              rel="noreferrer"
              data-testid="social-spotify"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-red-500 hover:text-red-500 transition px-3 py-2 text-xs uppercase tracking-[0.2em]"
            >
              <Music size={14} /> Spotify
            </a>
            <a
              href="https://www.youtube.com/@vincerodi84"
              target="_blank"
              rel="noreferrer"
              data-testid="social-youtube"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-red-500 hover:text-red-500 transition px-3 py-2 text-xs uppercase tracking-[0.2em]"
            >
              <Youtube size={14} /> YouTube
            </a>
          </div>
        </div>
      </div>

      {/* Sign off */}
      <div className="mt-20 text-center">
        <div className="font-serif-accent italic text-2xl md:text-3xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
          « Merci d'écouter, de venir, de partager — chaque message compte. »
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-[0.35em] text-red-500">— Vince</div>
      </div>
    </div>
  );
}
